import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const outlineToFillTypeSkill: Skill = {
    name: SKILL_NAMES.outlineToFillType,
    kind: 'codeo',
    description:
        'Draw a word as an outline stroke and then flood it with fill — for a wordmark, a title, ' +
        'or a name that should arrive as though it were being written and then set.',
    instructions: `# Outline To Fill Type

The letters are drawn as hollow strokes, one after another, and then colour floods in behind the outline. The two-stage arrival gives a title far more presence than a fade: the drawing establishes the shape and the fill commits to it.

Best on short, heavy words. Thin letterforms have too little interior for the fill to register, and long words take too long to draw.

## Markup

Use SVG text with a stroke and no fill, so the outline can be dash-drawn. Set the font from the template token as usual.

\`\`\`html
<svg id="wordmark" viewBox="0 0 1200 260" style="position: absolute; left: 360px; top: 400px; width: 1200px; height: 260px; overflow: visible;">
  <text id="wordmark-text" x="600" y="196" text-anchor="middle"
    style="font-family: var(--l5-font-0), sans-serif; font-size: 210px; font-weight: 900; letter-spacing: -0.02em;"
    fill="var(--l5-color-2, #38bdf8)" fill-opacity="0"
    stroke="var(--l5-color-2, #38bdf8)" stroke-width="3">NORTHWIND</text>
</svg>
\`\`\`

## Timeline code

The stroke draws with a dash offset; the fill opacity follows it in, slightly behind.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const wordEl = document.querySelector('#wordmark-text');
const wordLength = Math.ceil(wordEl.getComputedTextLength() * 2.6) + 2;

gsap.set(wordEl, { strokeDasharray: wordLength, strokeDashoffset: wordLength + 1, fillOpacity: 0 });

tl.to(wordEl, { strokeDashoffset: 0, duration: 1.15, ease: 'power2.inOut' }, 0.3);
tl.to(wordEl, { fillOpacity: 1, duration: 0.55, ease: 'power2.out' }, 1.1);

window.__timelines = { main: tl };
\`\`\`

\`getComputedTextLength\` measures the advance width, not the outline's path length, so it needs a multiplier — around 2.6 for a heavy face. Tune it until the draw completes exactly at the end of its tween.

## Adapt

- **Draw speed**: 0.9–1.3s. Longer and the viewer is waiting for a word they can already read from its outline.
- **Fill overlap**: starting the fill at about 80% of the draw makes the two stages read as one gesture. Waiting for the draw to finish makes them two separate events.
- **Fill from below**: instead of \`fill-opacity\`, a clipped rectangle rising behind the text floods the letters from the bottom — more physical, and worth the extra element on a hero title.
- **Keep the outline**: leaving the stroke visible at a contrasting colour gives an outlined-type look; dropping the stroke as the fill lands is cleaner.
- **Per-letter**: splitting into one \`<text>\` per character lets each draw and fill in sequence — much richer, and about three times the duration.
- **Line-art sibling**: for drawing a shape rather than type, use \`${SKILL_NAMES.svgDrawOn}\`.

## Pitfalls

- **Tween \`fill-opacity\`, never \`fill\`.** The letterforms already carry \`fill="var(--l5-color-N, …)"\`, so raising the opacity reveals the themed colour. Tweening \`fill\` to a literal like \`#ffffff\` both fails to interpolate into a var and permanently pins the wordmark off-palette — the one element in a video that should never stop following the brand.
- The dash length is an approximation of the glyph outlines' total path length. Verify the draw actually completes; if it finishes early, raise the multiplier. Keep the \`+ 2\` on the dash and the \`+ 1\` on the offset, which park the whole path inside the pattern's gap. A dash equal to the measured length puts the path's ends on the dash/gap boundary, where sub-unit slop paints a fraction of a unit that round caps blow up into a full-width dot.
- SVG text renders with the template font only if the font token is applied through \`style\`, exactly as in the markup above — a \`font-family\` presentation attribute will not pick up the CSS variable.
- The word must be short. Past about ten characters the draw is too slow and the stroke length estimate drifts badly.
- At light weights there is almost no interior to fill, so the second stage does nothing. Use 800+ weight.
- The SVG needs \`overflow: visible\` or descenders and wide letter-spacing get clipped.
- Check contrast in the outline-only state as well as the filled one — a thin stroke on a busy background is invisible for the first second.
- Once filled, the word is static; give the frame an ambient layer — see \`${SKILL_NAMES.driftAndHold}\`.`,
};
